const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object';

export const isSameFileId = (left: unknown, right: unknown) =>
  String(left) === String(right);

const fileIdOf = (item: unknown): string | null => {
  if (isRecord(item) && 'id' in item) {
    return String(item.id);
  }
  if (item == null || typeof item === 'object') {
    return null;
  }
  return String(item);
};

/**
 * Drop a chat-upload file from the inquiry payload used by the upload modal
 * and by document-id assembly. Returns the same object when nothing matches
 * so callers can avoid a needless re-render.
 */
export const removeFileFromInquiry = <T>(
  inquiry: T,
  fileId: string | number,
): T => {
  if (inquiry == null || typeof inquiry !== 'object') {
    return inquiry;
  }

  const current = inquiry as T & {
    attachments?: unknown;
    files?: unknown;
  };
  const target = String(fileId);
  const attachments = Array.isArray(current.attachments)
    ? current.attachments
    : null;
  const files = Array.isArray(current.files) ? current.files : null;

  const nextAttachments = attachments?.filter(
    item => fileIdOf(item) !== target,
  );
  const nextFiles = files?.filter(item => fileIdOf(item) !== target);

  const attachmentsChanged =
    attachments != null &&
    nextAttachments != null &&
    nextAttachments.length !== attachments.length;
  const filesChanged =
    files != null && nextFiles != null && nextFiles.length !== files.length;

  if (!attachmentsChanged && !filesChanged) {
    return inquiry;
  }

  return {
    ...current,
    ...(attachmentsChanged ? { attachments: nextAttachments } : {}),
    ...(filesChanged ? { files: nextFiles } : {}),
  } as T;
};

/** Re-apply every locally confirmed chip-list removal onto a server payload. */
export const applyLocalFileRemovals = <T>(
  inquiry: T,
  removedIds: Iterable<string | number>,
): T => {
  let next = inquiry;
  for (const id of removedIds) {
    next = removeFileFromInquiry(next, id);
  }
  return next;
};

export const excludeRemovedFileIds = <T>(
  items: T[],
  removedIds: Iterable<string | number>,
  idOf: (item: T) => unknown = item => item,
): T[] => {
  const removed = new Set(Array.from(removedIds, id => String(id)));
  if (removed.size === 0) {
    return items;
  }
  return items.filter(item => !removed.has(String(idOf(item))));
};
